import React, { useMemo, useState } from 'react';
import type { StudioAgreement } from '@brandclub/bc-widgets';

export interface ConciergeAgreementThemeColors {
  textPrimary?: string;
  primary?: string;
  buttonText?: string;
}

export interface ConciergeAgreementCardProps {
  agreement: StudioAgreement;
  /**
   * Optional user prompt to display alongside the agreement so the shopper
   * sees their pending question while reviewing the consent text.
   */
  pendingUserPrompt?: string;
  /** Brand-derived colors. Defaults are conservative neutral values. */
  themeColors?: ConciergeAgreementThemeColors;
  onAgree: () => void;
}

/**
 * Inline agreement / consent card shown after the shopper sends their first
 * question. The chat send is held until the shopper clicks the agree button.
 *
 * Body text supports these placeholder tokens which are substituted with
 * the matching URL/email fields from `StudioAgreement`:
 *   {termsOfUse} {privacyPolicy} {consumerHealth} {contactEmail}
 *
 * Three layouts (template-driven):
 *   - inline           — anchored to the bottom of the chat area (default)
 *   - centered-modal   — full-area overlay with backdrop and centered card
 *   - bottom-banner    — slim banner at the bottom of the chat (chat stays visible)
 */
const TOKEN_LABELS: Record<string, string> = {
  termsOfUse: 'Terms of Use',
  privacyPolicy: 'Privacy Policy',
  consumerHealth: 'Consumer Health Data Privacy Policy',
  contactEmail: '',
};

const TOKEN_PATTERN = /\{(termsOfUse|privacyPolicy|consumerHealth|contactEmail)\}/g;

function getTokenHref(token: string, agreement: StudioAgreement): string | null {
  switch (token) {
    case 'termsOfUse':
      return agreement.termsOfUseUrl || null;
    case 'privacyPolicy':
      return agreement.privacyPolicyUrl || null;
    case 'consumerHealth':
      return agreement.consumerHealthUrl || null;
    case 'contactEmail':
      return agreement.contactEmail ? `mailto:${agreement.contactEmail}` : null;
    default:
      return null;
  }
}

function getTokenLabel(token: string, agreement: StudioAgreement): string {
  if (token === 'contactEmail') return agreement.contactEmail || `{${token}}`;
  return TOKEN_LABELS[token] || `{${token}}`;
}

function renderBody(body: string, agreement: StudioAgreement, linkColor: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(body)) !== null) {
    if (match.index > cursor) {
      nodes.push(body.slice(cursor, match.index));
    }
    const token = match[1];
    const href = getTokenHref(token, agreement);
    const label = getTokenLabel(token, agreement);
    if (href) {
      const isMail = href.startsWith('mailto:');
      nodes.push(
        <a
          key={`tok-${key++}`}
          href={href}
          target={isMail ? undefined : '_blank'}
          rel={isMail ? undefined : 'noopener noreferrer'}
          style={{ color: linkColor, textDecoration: 'underline' }}
        >
          {label}
        </a>,
      );
    } else {
      nodes.push(match[0]);
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < body.length) nodes.push(body.slice(cursor));
  return nodes;
}

function renderParagraphs(
  body: string,
  agreement: StudioAgreement,
  textColor: string,
  linkColor: string,
): React.ReactNode[] {
  const paragraphs = body.split(/\n\s*\n/);
  return paragraphs.map((para, i) => (
    <p
      key={`p-${i}`}
      style={{
        margin: '0 0 12px',
        fontSize: 15,
        lineHeight: '22px',
        color: textColor,
        whiteSpace: 'pre-wrap',
      }}
    >
      {renderBody(para, agreement, linkColor)}
    </p>
  ));
}

interface LayoutProps {
  agreement: StudioAgreement;
  pendingUserPrompt?: string;
  buttonLabel: string;
  textColor: string;
  primaryColor: string;
  buttonTextColor: string;
  linkColor: string;
  onAgree: () => void;
}

const AgreeButton: React.FC<Pick<LayoutProps, 'buttonLabel' | 'primaryColor' | 'buttonTextColor' | 'onAgree'>> = ({
  buttonLabel,
  primaryColor,
  buttonTextColor,
  onAgree,
}) => (
  <button
    type="button"
    onClick={onAgree}
    style={{
      width: '100%',
      padding: '14px 16px',
      background: primaryColor,
      color: buttonTextColor,
      border: 'none',
      borderRadius: 4,
      fontSize: 15,
      fontWeight: 500,
      cursor: 'pointer',
    }}
  >
    {buttonLabel}
  </button>
);

const PendingPromptBubble: React.FC<{ text: string; primaryColor: string; buttonTextColor: string }> = ({
  text,
  primaryColor,
  buttonTextColor,
}) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
    <div
      style={{
        maxWidth: '80%',
        background: primaryColor,
        color: buttonTextColor,
        borderRadius: 20,
        padding: '10px 16px',
        fontSize: 15,
        lineHeight: '22px',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </div>
  </div>
);

const InlineLayout: React.FC<LayoutProps> = ({
  agreement,
  pendingUserPrompt,
  buttonLabel,
  textColor,
  primaryColor,
  buttonTextColor,
  linkColor,
  onAgree,
}) => {
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || '', agreement, textColor, linkColor),
    [agreement, textColor, linkColor],
  );

  return (
    <div
      role="region"
      aria-label={agreement.title || 'Agreement'}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        background: 'rgba(255,255,255,0.96)',
      }}
    >
      <div
        style={{
          padding: '24px 20px 20px',
          maxWidth: 720,
          width: '100%',
          margin: '0 auto',
          maxHeight: '100%',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {pendingUserPrompt ? (
          <PendingPromptBubble text={pendingUserPrompt} primaryColor={primaryColor} buttonTextColor={buttonTextColor} />
        ) : null}
        <div>
          {agreement.title ? (
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 15,
                lineHeight: '22px',
                fontWeight: 600,
                color: textColor,
              }}
            >
              {agreement.title}
            </p>
          ) : null}
          {rendered}
        </div>
        <AgreeButton
          buttonLabel={buttonLabel}
          primaryColor={primaryColor}
          buttonTextColor={buttonTextColor}
          onAgree={onAgree}
        />
      </div>
    </div>
  );
};

const CenteredModalLayout: React.FC<LayoutProps> = ({
  agreement,
  buttonLabel,
  textColor,
  primaryColor,
  buttonTextColor,
  linkColor,
  onAgree,
}) => {
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || '', agreement, textColor, linkColor),
    [agreement, textColor, linkColor],
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={agreement.title || 'Agreement'}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {agreement.title ? (
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              lineHeight: '24px',
              fontWeight: 700,
              color: textColor,
              textAlign: 'center',
            }}
          >
            {agreement.title}
          </h2>
        ) : null}

        <div>{rendered}</div>
        <AgreeButton
          buttonLabel={buttonLabel}
          primaryColor={primaryColor}
          buttonTextColor={buttonTextColor}
          onAgree={onAgree}
        />
      </div>
    </div>
  );
};

const BottomBannerLayout: React.FC<LayoutProps> = ({
  agreement,
  buttonLabel,
  textColor,
  primaryColor,
  buttonTextColor,
  linkColor,
  onAgree,
}) => {
  const [expanded, setExpanded] = useState(false);
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || '', agreement, textColor, linkColor),
    [agreement, textColor, linkColor],
  );

  const headline = agreement.title || 'Please review and accept to continue.';

  return (
    <div
      role="region"
      aria-label={agreement.title || 'Agreement'}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        background: '#fff',
        borderTop: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
        padding: '12px 16px',
        maxHeight: '60%',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              flex: 1,
              fontSize: 14,
              lineHeight: '20px',
              color: textColor,
              fontWeight: 600,
            }}
          >
            {headline}
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              color: linkColor,
              fontSize: 13,
              cursor: 'pointer',
              padding: '4px 8px',
              textDecoration: 'underline',
            }}
          >
            {expanded ? 'Hide' : 'Read more'}
          </button>
          <button
            type="button"
            onClick={onAgree}
            style={{
              padding: '8px 16px',
              background: primaryColor,
              color: buttonTextColor,
              border: 'none',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {buttonLabel}
          </button>
        </div>
        {expanded ? <div>{rendered}</div> : null}
      </div>
    </div>
  );
};

/**
 * Auto-Accept variant of the bottom banner: informational only, body always
 * visible, no agree button. A single Close action (styled like a text link)
 * dismisses the banner. Acceptance is recorded automatically when the
 * shopper sends their first message; this layout exists to inform them.
 */
const AutoAcceptBannerLayout: React.FC<LayoutProps> = ({
  agreement,
  textColor,
  linkColor,
  onAgree,
}) => {
  const rendered = useMemo(
    () => renderParagraphs(agreement.body || '', agreement, textColor, linkColor),
    [agreement, textColor, linkColor],
  );

  return (
    <div
      role="region"
      aria-label={agreement.title || 'Agreement'}
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 60,
        background: '#fff',
        borderTop: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
        padding: '12px 16px',
        maxHeight: '60%',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {agreement.title ? (
              <div
                style={{
                  fontSize: 14,
                  lineHeight: '20px',
                  color: textColor,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                {agreement.title}
              </div>
            ) : null}
            <div>{rendered}</div>
          </div>
          <button
            type="button"
            onClick={onAgree}
            style={{
              background: 'transparent',
              border: 'none',
              color: linkColor,
              fontSize: 13,
              cursor: 'pointer',
              padding: '4px 8px',
              textDecoration: 'underline',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const ConciergeAgreementCard: React.FC<ConciergeAgreementCardProps> = ({
  agreement,
  pendingUserPrompt,
  themeColors,
  onAgree,
}) => {
  const buttonLabel = agreement.agreeButtonLabel || 'I agree';
  const textColor = themeColors?.textPrimary || '#111827';
  const primaryColor = themeColors?.primary || '#000';
  const buttonTextColor = themeColors?.buttonText || '#fff';
  const linkColor = '#0E7490';

  const layout = agreement.template || 'inline';

  const layoutProps: LayoutProps = {
    agreement,
    pendingUserPrompt,
    buttonLabel,
    textColor,
    primaryColor,
    buttonTextColor,
    linkColor,
    onAgree,
  };

  if (layout === 'centered-modal') return <CenteredModalLayout {...layoutProps} />;
  if (layout === 'bottom-banner') return <BottomBannerLayout {...layoutProps} />;
  if (layout === 'auto-accept') return <AutoAcceptBannerLayout {...layoutProps} />;
  return <InlineLayout {...layoutProps} />;
};

export default ConciergeAgreementCard;
